import React, { useState, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ReactLoading from 'react-loading';
import { UncontrolledTooltip } from 'reactstrap';

import { clearBidHistory, fetchLotBidHistory, submitCancelBid } from '../../modules/bulkBidding';
import { formatTimestampWithTimezone } from '../../utilities/dates';
import { formatCurrency } from '../../utilities/helpers';

import DataTable from '../DataTable';

import './bulkBidding.scss';
import BidHistoryModal from './BidHistoryModal';
import DropDownBid from './DropDownBid';
import { ADMIN, CLOSED, INVALUABLE, LOT_CLOSES, LOT_EXTENDED, NON_RFC_CONNECT_TYPES, PRIVATE_LABEL } from './constants';

const Tooltip = ({ value, id }) => (
  <UncontrolledTooltip placement="bottom" target={id}>
    {value}
  </UncontrolledTooltip>
);

const CatalogView = ({ data }) => {
  const { catalogRef } = useParams();
  const dispatch = useDispatch();
  const selectedLotNumber = useRef(null);
  const cancelBidLotRef = useRef(null);
  const connectType = useSelector(state => state.user.connectType);
  const { lotsLoading, saleInfo: { timeZone, timeZoneCode } } = useSelector(state => state.bulkBidding);

  const [isBidHistoryModalOpen, setIsBidHistoryModalOpen] = useState(false);

  const isNonRFC = NON_RFC_CONNECT_TYPES.includes(connectType);

  const handleBidHistoryLinkClick = (lotRef, lotNumberWithExtension) => {
    setIsBidHistoryModalOpen(true);
    selectedLotNumber.current = lotNumberWithExtension;
    cancelBidLotRef.current = lotRef;
    dispatch(fetchLotBidHistory(lotRef, catalogRef));
  };

  const handleBidHistoryModalClose = () => {
    setIsBidHistoryModalOpen(false);
    dispatch(clearBidHistory());
  };

  const onBidCancel = (orderBidRef) => {
    dispatch(submitCancelBid({ catalogRef, orderBidRef, lotRef: cancelBidLotRef.current }));
    handleBidHistoryModalClose();
  };

  const renderHighBidderInfo = ({ maxBidderChannel, maxBidder, bidderEmail, lotNumberWithExtension }) => {
    if (!maxBidder) return '-';

    const isAdminBidder = maxBidderChannel === ADMIN;

    if (isAdminBidder) return ADMIN;

    const isPrivateLabelBidder = maxBidderChannel === PRIVATE_LABEL;

    return (
      <>
        <p className="d-flex mb-2">
          <span id={`max-bidder-name-${lotNumberWithExtension}`}>{maxBidder}</span>
          <Tooltip value={maxBidder} id={`max-bidder-name-${lotNumberWithExtension}`} />
          <span id={`max-bidder-channel-${lotNumberWithExtension}`}>
            {isPrivateLabelBidder ? '' : ` - ${maxBidderChannel}`}
          </span>
          <Tooltip
            value={isPrivateLabelBidder ? '' : `${maxBidderChannel}`}
            id={`max-bidder-channel-${lotNumberWithExtension}`}
          />
        </p>
        <span id={`max-bidder-email-${lotNumberWithExtension}`}>{bidderEmail}</span>
        <Tooltip value={bidderEmail} id={`max-bidder-email-${lotNumberWithExtension}`} />
      </>
    );
  };

  const renderCurrentBidInfo = ({ currentBid, lotRef, lotNumberWithExtension, bidCount }) => {
    const noBids = bidCount === 0;

    return (
      <div className="text-center">
        {noBids ? '-' : <div>{formatCurrency(currentBid)}</div>}
        <div
          className={`bids-link ${noBids ? 'disabled-link' : ''}`}
          onClick={() => handleBidHistoryLinkClick(lotRef, lotNumberWithExtension)}
        >
          {bidCount} Bids
        </div>
      </div>
    );
  };

  const headers = [
    {
      headerName: 'Lot',
      accessor: null,
      headerClassName: 'header-lot acc',
      bodyClassName: 'body-lot',
      Cell: ({ lotNumberWithExtension, lotImageUrl }) => (
        <div>
          <span>{lotNumberWithExtension}</span>
          {lotImageUrl && (
            <div className="bigImages">
              <img src={lotImageUrl.href} alt={lotNumberWithExtension} />
            </div>
          )}
        </div>
      )
    },
    {
      headerName: 'Description',
      accessor: null,
      headerClassName: 'header-lotDescription',
      bodyClassName: 'body-lotDescription',
      Cell: ({ lotDescription, lotUrl }) => (
        <a href={lotUrl} title={lotDescription} className="tableLink" rel="noopener noreferrer" target="_blank">
          {lotDescription}
        </a>
      )
    },
    {
      headerName: 'Consignor',
      accessor: null,
      headerClassName: `header-consignor ${isNonRFC ? 'd-none' : ''}`,
      bodyClassName: `body-consignor ${isNonRFC ? 'd-none' : ''}`,
      Cell: ({ consignor }) => <span className="consignor" title={consignor}>{consignor}</span>
    },
    {
      headerName: 'Est.',
      accessor: null,
      headerClassName: 'header-Est',
      bodyClassName: 'body-Est',
      Cell: ({ estimateLow, estimateHigh }) => `${formatCurrency(estimateLow)} - ${formatCurrency(estimateHigh)}`
    },
    {
      headerName: 'Reserve',
      accessor: null,
      headerClassName: 'header-reserve',
      bodyClassName: 'body-reserve',
      Cell: ({ reserve }) => formatCurrency(reserve)
    },
    {
      headerName: 'Current',
      accessor: null,
      headerClassName: 'header-currentBid',
      bodyClassName: 'body-currentBid',
      Cell: renderCurrentBidInfo
    },
    {
      headerName: 'Max',
      accessor: null,
      headerClassName: 'header-maxBid',
      bodyClassName: 'body-maxBid',
      Cell: ({ maxBid, maxBidderChannel }) => {
        if (maxBidderChannel === INVALUABLE || maxBid === 0) return '-';
        return formatCurrency(maxBid);
      }
    },
    {
      headerName: 'High Bidder',
      accessor: null,
      headerClassName: 'header-maxBidder',
      bodyClassName: 'body-maxBidder',
      Cell: renderHighBidderInfo
    },
    {
      headerName: 'Status',
      accessor: null,
      headerClassName: 'header-status',
      bodyClassName: 'body-status',
      Cell: ({ isClosed, isExtended, endTime }) => {
        if (isClosed) return CLOSED;

        const displayMessage = isExtended ? LOT_EXTENDED : LOT_CLOSES;

        return (
          <>
            <p className="mb-0">{displayMessage}</p>
            <span>{formatTimestampWithTimezone(endTime, timeZone, timeZoneCode)}</span>
          </>
        );
      }
    },
    {
      headerName: 'New Bid',
      accessor: null,
      headerClassName: 'header-newBid',
      bodyClassName: 'body-newBid',
      Cell: ({ bidIncrements, lotRef, isClosed }) => (
        <DropDownBid
          disabled={isClosed}
          lotRef={lotRef}
          bidIncrements={bidIncrements}
          canUnSelect={true}
        />
      )
    }
  ];

  return (
    <div className="my-4 data-table-wrapper">
      {lotsLoading ? (
        <ReactLoading className="loading-spinner m-auto" type="spin" color="black" />
      ) : (
        <DataTable
          headers={headers}
          dataItems={data}
          headerClassName="table-header"
          rowClassName="table-row"
        />
      )}
      { isBidHistoryModalOpen && (
        <BidHistoryModal
          lotNumber={selectedLotNumber.current}
          isOpen={isBidHistoryModalOpen}
          onClose={handleBidHistoryModalClose}
          onBidCancel={onBidCancel}
        />
      )}
    </div>
  );
};

export default memo(CatalogView);
